import { Dayts, diff, isNil, queryInput } from '@/utils';
import { PermissionHasUserEffectEnum, RunUpdateUserProfileInput, User } from '@/models/gen/graphql';

import { RunUpdateUserProfileBulkDocument } from '@/api/queries';
import graphApi from '@/api';

const [runUpdateUserProfileBulk] = graphApi(RunUpdateUserProfileBulkDocument);

// Airport-permission entries are `{ id, airportCode, permissionId }` on a loaded user but collapse to
// `{ permissionId }` once the field is edited - so the only stable identity is `permissionId`.
const permissionIdOf = (node: any): string => node?.permissionId || node?.id || node;

const updateUserBulk = async (ids: string[], payload: Partial<User>, user?: Partial<User>) => {
  const { added, deleted, updated } = diff(user, payload);

  const ownerId = ids.at(0);
  const isBulk = ids.length > 1;

  // The generic object diff keys arrays by `item.id`; airports lose their id on edit and licenses drop
  // the id from the changed-field partial, so relational changes are diffed explicitly here instead.
  const originalPermissions = (user?.airportCodes || []) as any[];
  const nextPermissions = (payload?.airportCodes || []) as any[];
  const permissionsToCreate = !isBulk
    ? nextPermissions.filter((next) => !originalPermissions.some((prev) => permissionIdOf(prev) === permissionIdOf(next)))
    : [];
  const permissionsToDelete = !isBulk
    ? originalPermissions.filter((prev) => !nextPermissions.some((next) => permissionIdOf(prev) === permissionIdOf(next)))
    : [];

  const originalLicenseIds = new Set((user?.licenses || []).map((license: any) => license?.id).filter(Boolean));

  const isCreatingLicenses = !isBulk && Object.keys(added?.licenses || {}).length > 0;
  const isDeletingLicenses = !isBulk && Object.keys(deleted?.licenses || {}).length > 0;
  const isUpdatingLicenses = !isBulk && Object.keys(updated?.licenses || {}).length > 0;
  const isUpdatingGroups = !isBulk && Object.keys(added?.groups || {}).length > 0;
  const isDeletingGroups = !isBulk && Object.keys(deleted?.groups || {}).length > 0;
  const updates: RunUpdateUserProfileInput[] = [
    {
      // General User Updates
      query: [{ id: queryInput(ids) }],
      updateUserValues: {
        active: !isNil(updated.active) ? [updated.active ? 1 : 0] : null,
        address: updated.address ? [updated.address] : null,
        avatar: updated.avatar ? [updated.avatar] : null,
        cityName: updated.cityName ? [updated.cityName] : null,
        companyId: updated.companyId ? [updated.companyId] : null,
        countryCode: updated.countryCode ? [updated.countryCode] : null,
        dob: updated.dob ? [new Dayts(updated.dob).format()] : null,
        email: updated.email && !isBulk ? [updated.email] : null,
        employeeId: updated.employeeId && !isBulk ? [updated.employeeId] : null,
        firstName: updated.firstName ? [updated.firstName] : null,
        lastName: updated.lastName ? [updated.lastName] : null,
        longTrip: !isNil(updated.longTrip) ? [updated.longTrip ? 1 : 0] : null,
        notes: updated.notes ? [updated.notes] : null,
        password: updated.password ? [updated.password] : null,
        phone: updated.phone ? [updated.phone.replace(/\D/g, '')] : null,
        primaryAirportCode: updated.primaryAirportCode ? [updated.primaryAirportCode] : null,
        providerId: updated.providerId ? [updated.providerId] : null,
        roleId: updated.roleId ? [updated.roleId] : null,
        startDate: updated.startDate ? [new Dayts(updated.startDate).format()] : null,
        stateCode: updated.stateCode ? [updated.stateCode] : null,
        username: updated.username && !isBulk ? [updated.username] : null,
        zipCode: updated.zipCode ? [updated.zipCode] : null,
      },
      // Create Licenses
      createLicenseBulk: isCreatingLicenses
        ? Object.entries(added.licenses || {}).map(([, license]) => ({
            userId: ownerId,
            number: license.number ? license.number : null,
            licenseTypeId: license.licenseTypeId ? license.licenseTypeId : null,
            expirationDate: license.expirationDate ? new Dayts(license.expirationDate).format() : null,
            stateCode: license.stateCode ? license.stateCode : null,
          }))
        : null,
      // Delete Licenses - only rows that existed on the loaded user (never a synthetic index key).
      deleteLicenseBulk: isDeletingLicenses
        ? Object.keys(deleted.licenses || {})
            .filter((id) => originalLicenseIds.has(id))
            .map((id) => ({ id }))
        : null,
      // Update Licenses - the changed-field partial has no `id`, so use the diff key (the license id).
      updateLicenseBulk: isUpdatingLicenses
        ? Object.entries(updated.licenses || {})
            .filter(([id, license]) => !!license && originalLicenseIds.has(id))
            .map(([id, license]) => ({
              query: [{ id: queryInput(id) }],
              value: {
                number: license.number ? [license.number] : null,
                licenseTypeId: license.licenseTypeId ? [license.licenseTypeId] : null,
                expirationDate: license.expirationDate ? [new Dayts(license.expirationDate).format()] : null,
                stateCode: license.stateCode ? [license.stateCode] : null,
              },
            }))
        : null,
      // Airport permissions - explicit add/remove diff so untouched permissions are left alone.
      createPermissionHasUserBulk: permissionsToCreate.length
        ? permissionsToCreate.map((permission) => ({
            userId: ownerId,
            permissionId: permissionIdOf(permission),
            effect: PermissionHasUserEffectEnum.Allow,
          }))
        : null,
      deletePermissionHasUserBulk: permissionsToDelete.length
        ? permissionsToDelete.map((permission) => ({
            userId: ownerId,
            permissionId: permissionIdOf(permission),
          }))
        : null,
      createGroupHasUserBulk: isUpdatingGroups
        ? Object.values(added.groups || {}).map(({ id }) => ({
            userId: ownerId,
            groupId: id,
          }))
        : null,
      deleteGroupHasUserBulk: isDeletingGroups
        ? Object.keys(deleted.groups || {}).map((groupId) => ({
            userId: ownerId,
            groupId,
          }))
        : null,
    },
  ];
  const res = await runUpdateUserProfileBulk({
    input: { updates },
  });
  return res;
};

export default updateUserBulk;
